import { useEffect, useMemo, useState, useRef, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { api, API, formatINR, formatCompact } from "@/lib/api";
import { toast } from "sonner";
import AIDrawer from "@/components/AIDrawer";
import ItemMovementsDialog from "@/components/ItemMovementsDialog";
import { Download, FileSpreadsheet, Loader2, Upload, Trash2, FileDown } from "lucide-react";

const fmtD = (v) => new Date(v).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
const fq = (v) => (v ? v.toLocaleString("en-IN", { maximumFractionDigits: 2 }) : "—");
const fv = (v) => (v ? formatCompact(v) : "—");

export default function StockRecoPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [fromM, setFromM] = useState("");
  const [toM, setToM] = useState("");
  const [plant, setPlant] = useState("");
  const [material, setMaterial] = useState("");
  const [mvtType, setMvtType] = useState("");
  const [tab, setTab] = useState("reco");
  const [drill, setDrill] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [uploads, setUploads] = useState(null);
  const [uploading, setUploading] = useState("");
  const fileRefs = { mb5b: useRef(), mb51: useRef(), sales: useRef() };

  const loadReport = () => {
    Promise.all([
      api.get("/reports/stock-reco"),
      api.get("/system/config").catch(() => ({ data: {} })),
    ]).then(([{ data }, cfg]) => {
      setData(data);
      const now = new Date();
      const curLabel = `${now.toLocaleString("en-US", { month: "short" })} ${now.getFullYear()}`;
      let def = data.months[0];
      if (cfg.data.data_start) {
        const d = new Date(cfg.data.data_start + "T00:00:00");
        const label = `${d.toLocaleString("en-US", { month: "short" })} ${d.getFullYear()}`;
        if (data.months.includes(label)) def = label;
      }
      if (data.months.includes(curLabel)) def = curLabel;  // default view: current month
      setFromM(def);
      setToM(data.months.includes(curLabel) ? curLabel : data.months[data.months.length - 1]);
    }).catch((e) => setError(e.response?.data?.detail || e.message));
  };
  const loadUploads = () => api.get("/stock/uploads").then(({ data }) => setUploads(data)).catch(() => {});

  useEffect(() => {
    loadReport();
    loadUploads();
  }, []);

  const setSource = async (source) => {
    try {
      const { data: r } = await api.post("/stock/source", { source });
      toast.success(r.message);
      setUploads((u) => ({ ...u, source }));
      setData(null);
      loadReport();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const CHUNK = 3 * 1024 * 1024;

  const uploadFile = async (kind, file) => {
    if (!file) return;
    setUploading(kind);
    try {
      let r;
      if (file.size <= CHUNK) {
        const fd = new FormData();
        fd.append("file", file);
        ({ data: r } = await api.post(`/stock/upload/${kind}`, fd, { headers: { "Content-Type": "multipart/form-data" } }));
      } else {
        // big exports go up in small slices so proxy body limits never reject them
        const total = Math.ceil(file.size / CHUNK);
        const uploadId = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
        const progress = toast.loading(`Uploading ${file.name} — 0/${total}`);
        try {
          for (let i = 0; i < total; i++) {
            const fd = new FormData();
            fd.append("file", file.slice(i * CHUNK, (i + 1) * CHUNK), `part${i}`);
            fd.append("upload_id", uploadId);
            fd.append("chunk_index", String(i));
            fd.append("total_chunks", String(total));
            fd.append("filename", file.name);
            ({ data: r } = await api.post(`/stock/upload-chunk/${kind}`, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 180000 }));
            if (!r.done && !r.processing) toast.loading(`Uploading ${file.name} — ${i + 1}/${total}`, { id: progress });
          }
          if (r.processing && r.job_id) {
            // parsing happens server-side in the background — poll until it finishes
            const t0 = Date.now();
            for (;;) {
              const mins = Math.floor((Date.now() - t0) / 60000);
              toast.loading(`Processing ${file.name}… ${mins > 0 ? `(${mins} min)` : "this can take a few minutes for large files"}`, { id: progress });
              await new Promise((res) => setTimeout(res, 4000));
              const { data: st } = await api.get(`/stock/upload-status/${r.job_id}`);
              if (st.status === "done") { r = st.result; break; }
              if (st.status === "failed") throw new Error(st.error || "Upload processing failed");
              if (Date.now() - t0 > 25 * 60 * 1000) throw new Error("Processing timed out — please retry");
            }
          }
        } finally {
          toast.dismiss(progress);
        }
      }
      toast.success(r.message);
      loadUploads();
      if (uploads?.source === "upload") { setData(null); loadReport(); }
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setUploading("");
      if (fileRefs[kind].current) fileRefs[kind].current.value = "";
    }
  };

  const clearUploads = async () => {
    try {
      const { data: r } = await api.delete("/stock/uploads");
      toast.success(r.message);
      loadUploads();
      if (uploads?.source === "upload") { setData(null); loadReport(); }
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const downloadTemplate = async (kind) => {
    const res = await fetch(`${API}/stock/template/${kind}`, { credentials: "include" });
    const blob = await res.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = `DeePiping_${kind}_template.xlsx`; a.click();
  };

  const cats = data?.categories || [];
  const catKeys = useMemo(() => cats.map(([k]) => k), [cats]);

  const { monthRange, monthsBefore, monthsAfter } = useMemo(() => {
    if (!data) return { monthRange: [], monthsBefore: [], monthsAfter: [] };
    const i1 = data.months.indexOf(fromM), i2 = data.months.indexOf(toM);
    const a = Math.min(i1 < 0 ? 0 : i1, i2 < 0 ? data.months.length - 1 : i2);
    const b = Math.max(i1 < 0 ? 0 : i1, i2 < 0 ? data.months.length - 1 : i2);
    return { monthRange: data.months.slice(a, b + 1), monthsBefore: data.months.slice(0, a), monthsAfter: data.months.slice(b + 1) };
  }, [data, fromM, toM]);

  const { monthBook, mb5bSet } = useMemo(() => {
    const mb = {};
    (data?.month_book || []).forEach((g) => (g.rows || []).forEach(([mat, pl, cq, cv, oq, ov]) => {
      mb[`${mat}|${pl}|${g.month}`] = { cq, cv, oq, ov };
    }));
    return { monthBook: mb, mb5bSet: new Set(data?.mb5b_months || []) };
  }, [data]);

  const recoRows = useMemo(() => {
    if (!data) return [];
    const bookMap = {};
    data.book.forEach((b) => { bookMap[`${b.material}|${b.plant}`] = b; });
    const agg = {};
    const matches = (m) => (!plant || m.plant === plant) && (!material || m.material === material);
    data.monthly.forEach((m) => {
      if (!matches(m)) return;
      const k = `${m.material}|${m.plant}`;
      const p = agg[k] || (agg[k] = { material: m.material, description: m.description, plant: m.plant, uom: m.uom,
        cats: Object.fromEntries(catKeys.map((c) => [c, [0, 0]])), afterQty: 0, afterVal: 0, beforeQty: 0, beforeVal: 0 });
      catKeys.forEach((c) => {
        const q = m[`${c}_qty`] || 0, v = m[`${c}_value`] || 0;
        if (monthRange.includes(m.month)) { p.cats[c][0] += q; p.cats[c][1] += v; }
        else if (monthsAfter.includes(m.month)) { p.afterQty += q; p.afterVal += v; }
        else if (monthsBefore.includes(m.month)) { p.beforeQty += q; p.beforeVal += v; }
      });
    });
    // include materials with book stock but no movements in range
    data.book.forEach((b) => {
      if ((plant && b.plant !== plant) || (material && b.material !== material)) return;
      const k = `${b.material}|${b.plant}`;
      if (!agg[k]) agg[k] = { material: b.material, description: b.material, plant: b.plant, uom: "",
        cats: Object.fromEntries(catKeys.map((c) => [c, [0, 0]])), afterQty: 0, afterVal: 0, beforeQty: 0, beforeVal: 0 };
    });
    return Object.values(agg).map((p) => {
      const book = bookMap[`${p.material}|${p.plant}`] || { actual_qty: 0, actual_value: 0, rate: 0, opening_qty: 0, opening_value: 0, has_open: false };
      const netQty = catKeys.reduce((s, c) => s + p.cats[c][0], 0);
      const netVal = catKeys.reduce((s, c) => s + p.cats[c][1], 0);
      let actualQty = book.actual_qty - p.afterQty;
      let actualVal = book.actual_value - p.afterVal;
      if (monthsAfter.length && mb5bSet.has(toM)) {
        // real MB5B closing balance of the selected To month — price diff becomes range-true
        const ce = monthBook[`${p.material}|${p.plant}|${toM}`];
        actualQty = ce ? ce.cq : 0; actualVal = ce ? ce.cv : 0;
      }
      let openingQty, openingVal;
      const oe = mb5bSet.has(fromM) ? monthBook[`${p.material}|${p.plant}|${fromM}`] : null;
      if (mb5bSet.has(fromM)) {
        // real MB5B opening balance of the selected From month
        openingQty = oe ? oe.oq : 0; openingVal = oe ? oe.ov : 0;
      } else if (book.has_open) {
        // exact MB5B opening at window start, rolled forward through months before the selected From
        openingQty = (book.opening_qty || 0) + (p.beforeQty || 0);
        openingVal = (book.opening_value || 0) + (p.beforeVal || 0);
      } else {
        openingQty = actualQty - netQty;
        openingVal = openingQty * book.rate;
      }
      const derivedQty = openingQty + netQty;
      const derivedVal = openingVal + netVal;
      return { ...p, rate: book.rate,
        opening: [openingQty, openingVal], derived: [derivedQty, derivedVal],
        actual: [actualQty, actualVal], price_diff: actualVal - derivedVal };
    }).sort((a, b) => Math.abs(b.actual[1]) - Math.abs(a.actual[1]));
  }, [data, monthRange, monthsBefore, monthsAfter, plant, material, catKeys, monthBook, mb5bSet, fromM, toM]);

  const totals = useMemo(() => {
    const t = { opening: [0, 0], derived: [0, 0], actual: [0, 0], price_diff: 0,
      cats: Object.fromEntries(catKeys.map((c) => [c, [0, 0]])) };
    recoRows.forEach((r) => {
      t.opening[0] += r.opening[0]; t.opening[1] += r.opening[1];
      t.derived[0] += r.derived[0]; t.derived[1] += r.derived[1];
      t.actual[0] += r.actual[0]; t.actual[1] += r.actual[1];
      t.price_diff += r.price_diff;
      catKeys.forEach((c) => { t.cats[c][0] += r.cats[c][0]; t.cats[c][1] += r.cats[c][1]; });
    });
    return t;
  }, [recoRows, catKeys]);

  const movRows = useMemo(() => {
    if (!data) return [];
    return data.movements.filter((m) =>
      monthRange.includes(m.month) && (!plant || m.plant === plant) &&
      (!material || m.material === material) && (!mvtType || m.mtype === mvtType)).slice(0, 500);
  }, [data, monthRange, plant, material, mvtType]);

  const marginRows = useMemo(
    () => (data?.margin ? data.margin.rows.filter((r) => !material || r.material === material) : []),
    [data, material]);
  const activeCat = tab.startsWith("cat:") ? tab.slice(4) : null;
  const catRows = useMemo(() => (activeCat
    ? recoRows.filter((r) => r.cats[activeCat][0] || r.cats[activeCat][1])
        .sort((a, b) => Math.abs(b.cats[activeCat][1]) - Math.abs(a.cats[activeCat][1]))
    : []), [recoRows, activeCat]);
  const catTotal = useMemo(() => catRows.reduce((t, r) => [t[0] + r.cats[activeCat][0], t[1] + r.cats[activeCat][1]], [0, 0]), [catRows, activeCat]);
  const pdRows = useMemo(() => recoRows.filter((r) => Math.abs(r.price_diff) > 1)
    .sort((a, b) => Math.abs(b.price_diff) - Math.abs(a.price_diff)), [recoRows]);
  const pdTotal = useMemo(() => pdRows.reduce((s, r) => s + r.price_diff, 0), [pdRows]);
  const marginTotals = useMemo(() => {
    const t = { billed_qty: 0, billed_value: 0, cogs_qty: 0, cogs_value: 0, margin: 0 };
    marginRows.forEach((r) => { Object.keys(t).forEach((k) => { t[k] += r[k] || 0; }); });
    t.margin_pct = t.billed_value ? Math.round((1000 * t.margin) / t.billed_value) / 10 : 0;
    return t;
  }, [marginRows]);

  const ageingParams = useMemo(() => {
    const p = new URLSearchParams();
    if (data?.months?.length && toM && toM !== data.months[data.months.length - 1]) {
      const d = new Date(`1 ${toM}`);
      if (!isNaN(d)) {
        const last = new Date(d.getFullYear(), d.getMonth() + 1, 0);
        p.set("as_of", `${last.getFullYear()}-${String(last.getMonth() + 1).padStart(2, "0")}-${String(last.getDate()).padStart(2, "0")}`);
      }
    }
    if (plant) p.set("plant", plant);
    if (material) p.set("q", material);
    const s = p.toString();
    return s ? `?${s}` : "";
  }, [data, toM, plant, material]);

  const exportCsv = () => {
    let head, body, name = "DeePiping_stock-reco";
    if (activeCat) {
      const l = (cats.find(([k]) => k === activeCat) || ["", activeCat])[1];
      head = ["Material", "Description", "Plant", "UoM", `${l} Qty`, `${l} Value`, "Avg Rate"].join(",");
      body = catRows.map((r) => [r.material, `"${r.description}"`, `"${r.plant}"`, r.uom,
        r.cats[activeCat][0], Math.round(r.cats[activeCat][1]),
        r.cats[activeCat][0] ? Math.round((100 * Math.abs(r.cats[activeCat][1] / r.cats[activeCat][0])) ) / 100 : ""].join(",")).join("\n");
      name = `DeePiping_stock-${activeCat}`;
    } else if (tab === "pricediff") {
      head = ["Material", "Description", "Plant", "UoM", "Derived Qty", "Derived Val", "Actual Qty", "Actual Val", "Price Diff"].join(",");
      body = pdRows.map((r) => [r.material, `"${r.description}"`, `"${r.plant}"`, r.uom,
        r.derived[0], Math.round(r.derived[1]), r.actual[0], Math.round(r.actual[1]), Math.round(r.price_diff)].join(",")).join("\n");
      name = "DeePiping_stock-price-diff";
    } else {
      head = ["Material", "Description", "Plant", "UoM",
        "Opening Qty", "Opening Val", ...cats.flatMap(([, l]) => [`${l} Qty`, `${l} Val`]),
        "Derived Qty", "Derived Val", "Actual Qty", "Actual Val", "Price Diff"].join(",");
      body = recoRows.map((r) => [r.material, `"${r.description}"`, `"${r.plant}"`, r.uom,
        r.opening[0], Math.round(r.opening[1]), ...catKeys.flatMap((c) => [r.cats[c][0], Math.round(r.cats[c][1])]),
        r.derived[0], Math.round(r.derived[1]), r.actual[0], Math.round(r.actual[1]), Math.round(r.price_diff)].join(",")).join("\n");
    }
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([head + "\n" + body], { type: "text/csv" }));
    a.download = `${name}.csv`; a.click();
  };
  const exportExcel = async () => {
    setExporting(true);
    try {
      const q = new URLSearchParams({ from_month: fromM || "", to_month: toM || "", plant: plant || "", material: material || "" });
      if (activeCat) q.set("category", activeCat);
      else if (tab === "pricediff") q.set("category", "price_diff");
      const res = await fetch(`${API}/reports/stock-reco/export?${q}`, { credentials: "include" });
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `DeePiping_stock-reco${activeCat ? `_${activeCat}` : tab === "pricediff" ? "_price-diff" : ""}.xlsx`; a.click();
    } finally { setExporting(false); }
  };

  if (error) return <div className="text-sm text-amber-800 bg-amber-50 border border-amber-300 rounded-md p-4 max-w-lg">{error}</div>;
  if (!data) return <div className="text-slate-500 text-sm animate-pulse">Loading stock reconciliation…</div>;

  const input = "bg-white border border-slate-300 rounded-md px-3 py-1.5 text-sm text-slate-800 focus:outline-none focus:border-[#B01E24]/50";
  const otherCons = totals.cats.prod_cons[1] + totals.cats.direct_cons[1] + totals.cats.misc[1];
  const kpiCards = [
    ["Opening Stock", totals.opening[1], "", "opening", "reco", ""],
    ["Purchase", totals.cats.purchases[1] + totals.cats.direct_inward[1], "", "purchase", "cat:purchases", "purchases + direct inward"],
    ["Other Consumption", otherCons, "", "other-cons", "cat:direct_cons", "prod + direct + misc cons"],
    ["Sales Consumption", totals.cats.sales[1], "", "sales-cons", "cat:sales", ""],
    ["Derived Closing", totals.derived[1], "border-cyan-200 bg-cyan-50/40", "derived", "reco", ""],
    ["Price Difference", totals.price_diff, "border-amber-300 bg-amber-50/50", "price-diff", "pricediff", ""],
    ["Actual Closing", totals.actual[1], "border-emerald-200 bg-emerald-50/40", "actual", "ageing", "click to age this closing stock"],
  ];

  return (
    <div className="space-y-5" data-testid="report-page-stock-reco">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">Inventory — Stock Reconciliation</h1>
          <p className="text-sm text-slate-500 mt-1 flex items-center gap-2">
            MB51 movements by category → derived closing vs SAP stock book; the difference is mainly price difference · As of {data.as_of}
            <span data-testid="stock-reco-source-chip" className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${data.data_source === "SAP" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : data.data_source === "UPLOAD" ? "bg-sky-50 text-sky-700 border-sky-200" : "bg-amber-50 text-amber-700 border-amber-300"}`}>
              {data.data_source === "SAP" ? "SAP Live · Read-only" : data.data_source === "UPLOAD" ? "Uploaded Files" : "Mock Data"}
            </span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={exportCsv} data-testid="stock-reco-export-csv-button" className="flex items-center gap-1.5 text-xs bg-white hover:bg-slate-50 text-slate-700 border border-slate-300 rounded-md px-3 py-1.5 transition-colors"><Download size={12} /> CSV</button>
          <button onClick={exportExcel} disabled={exporting} data-testid="stock-reco-export-excel-button" className="flex items-center gap-1.5 text-xs bg-emerald-700/90 hover:bg-emerald-600 disabled:opacity-60 text-white rounded-md px-3 py-1.5 transition-colors">{exporting ? <Loader2 size={12} className="animate-spin" /> : <FileSpreadsheet size={12} />} Excel</button>
          <AIDrawer reportKey="stock-reco" reportTitle="Stock Reconciliation" />
        </div>
      </div>

      {data.live_pending && (
        <div className="rounded-lg border border-sky-300 bg-sky-50 p-3 text-sm text-sky-800" data-testid="stock-reco-live-pending">
          Live SAP extraction is running in the background — this page shows sample data meanwhile. Refresh in a minute or two.
        </div>
      )}

      {uploads && (
        <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-5 space-y-4" data-testid="stock-uploads-panel">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <span className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">Uploaded SAP Exports</span>
              <span className="text-xs text-slate-400" data-testid="stock-uploads-summary">
                {uploads.months.length} month{uploads.months.length === 1 ? "" : "s"} loaded · {uploads.totals.mb5b.toLocaleString("en-IN")} stock rows · {uploads.totals.mb51.toLocaleString("en-IN")} movement rows · {uploads.totals.sales.toLocaleString("en-IN")} sales rows
              </span>
            </div>
            <div className="flex items-center rounded-md border border-slate-300 overflow-hidden" data-testid="stock-source-toggle">
              <span className="px-2.5 text-[11px] text-slate-400 select-none">Data source</span>
              {[["sap", "Extract from SAP"], ["upload", "Uploaded files"]].map(([k, l]) => (
                <button key={k} onClick={() => setSource(k)} data-testid={`stock-source-${k}`}
                  className={`px-3 py-1.5 text-xs font-medium transition-colors ${uploads.source === k ? "bg-[#B01E24] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>{l}</button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[["mb5b", "Stock Balance (MB5B)"], ["mb51", "Material Documents (MB51)"], ["sales", "Item Sales (Billing)"]].map(([k, l]) => (
              <div key={k} className="border border-slate-200 rounded-md p-3">
                <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-2">{l}</div>
                <div className="flex items-center gap-2">
                  <input ref={fileRefs[k]} type="file" accept=".xlsx,.xls,.csv" className="hidden" data-testid={`upload-input-${k}`}
                    onChange={(e) => uploadFile(k, e.target.files?.[0])} />
                  <button onClick={() => fileRefs[k].current?.click()} disabled={!!uploading} data-testid={`upload-button-${k}`}
                    className="flex items-center gap-1.5 text-xs font-medium bg-[#14202E] hover:bg-[#1d2d40] disabled:opacity-60 text-white rounded-md px-3 py-1.5 transition-colors">
                    {uploading === k ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />} Choose File
                  </button>
                  <button onClick={() => downloadTemplate(k)} data-testid={`template-button-${k}`}
                    className="flex items-center gap-1.5 text-xs bg-white hover:bg-slate-50 text-slate-600 border border-slate-300 rounded-md px-3 py-1.5 transition-colors">
                    <FileDown size={12} /> Template
                  </button>
                </div>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {uploads.months.map((m) => (
              <span key={m.month} className="inline-flex items-center gap-2 text-[11px] border border-slate-200 rounded-full px-3 py-1 bg-slate-50" data-testid={`upload-month-${m.month.replace(" ", "-")}`}>
                <b className="text-slate-700">{m.month}</b>
                <span className={m.mb5b ? "text-emerald-600" : "text-slate-300"}>MB5B {m.mb5b ? "✓" : "—"}</span>
                <span className={m.mb51 ? "text-emerald-600" : "text-slate-300"}>MB51 {m.mb51 ? "✓" : "—"}</span>
                <span className={m.sales ? "text-emerald-600" : "text-slate-300"}>Sales {m.sales ? "✓" : "—"}</span>
              </span>
            ))}
            {uploads.months.length > 0 && (
              <button onClick={clearUploads} data-testid="clear-uploads-button"
                className="inline-flex items-center gap-1.5 text-xs text-[#B01E24] border border-[#B01E24]/30 rounded-md px-3 py-1 hover:bg-[#B01E24]/5 transition-colors">
                <Trash2 size={12} /> Clear uploaded
              </button>
            )}
          </div>
          <p className="text-[11px] text-slate-400">
            Re-uploading a month replaces just that month. In "Uploaded files" mode the reconciliation runs on MB51 movements, MB5B closing balances (qty + value) and billing rows — Opening comes from the first month, Closing from the last. Download a template for the expected columns.
          </p>
        </div>
      )}

      <div className="flex flex-wrap items-end gap-3" data-testid="stock-reco-filters">
        <div><div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">From Month</div>
          <select value={fromM} onChange={(e) => setFromM(e.target.value)} data-testid="stock-reco-from-month" className={input}>{data.months.map((m) => <option key={m}>{m}</option>)}</select></div>
        <div><div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">To Month</div>
          <select value={toM} onChange={(e) => setToM(e.target.value)} data-testid="stock-reco-to-month" className={input}>{data.months.map((m) => <option key={m}>{m}</option>)}</select></div>
        <div><div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">Plant</div>
          <select value={plant} onChange={(e) => setPlant(e.target.value)} data-testid="stock-reco-plant-select" className={input}><option value="">All Plants</option>{data.plants.map((p) => <option key={p}>{p}</option>)}</select></div>
        <div><div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">Material</div>
          <select value={material} onChange={(e) => setMaterial(e.target.value)} data-testid="stock-reco-material-select" className={input}><option value="">All Materials</option>{data.materials.map((m) => <option key={m}>{m}</option>)}</select></div>
        <div><div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">Movement Type (MB51 register)</div>
          <select value={mvtType} onChange={(e) => setMvtType(e.target.value)} data-testid="stock-reco-mvt-select" className={input}><option value="">All Movement Types</option>{data.mvt_types.map((m) => <option key={m}>{m}</option>)}</select></div>
        <div className="ml-auto text-xs text-slate-500">{recoRows.length} materials · {monthRange[0]} – {monthRange[monthRange.length - 1]}</div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-7 gap-3" data-testid="stock-reco-kpis">
        {kpiCards.map(([label, val, extra, tid, target, sub]) => (
          <button key={label} onClick={() => (target === "ageing" ? navigate(`/reports/stock-ageing${ageingParams}`) : setTab(target))} className={`rounded-lg border border-slate-200 bg-white shadow-sm p-3.5 text-left cursor-pointer hover:border-[#B01E24]/40 hover:shadow transition-all ${extra}`} data-testid={`stock-kpi-${tid}`} title={target === "ageing" ? "Open Inventory Ageing for this closing stock" : "Click to drill down"}>
            <div className="text-[9.5px] font-semibold uppercase tracking-widest text-slate-500">{label}</div>
            <div className={`mt-1 text-lg font-bold font-mono ${label === "Price Difference" && Math.abs(val) > 0 ? "text-amber-700" : "text-slate-900"}`}>{formatCompact(val)}</div>
            <div className="text-[10px] text-slate-400">{sub || "mixed units"}</div>
          </button>
        ))}
      </div>

      <div className="flex flex-wrap border-b border-slate-200 gap-1" data-testid="stock-reco-tabs">
        {[["reco", "Reconciliation"],
          ...cats.map(([k, l]) => [`cat:${k}`, l]),
          ["pricediff", "Price Difference"],
          ["mb51", "Movement Register (MB51)"], ["margin", "Sales & Margin"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`stock-reco-tab-${k.replace(":", "-")}`}
            className={`px-3 py-2 text-[12.5px] font-medium border-b-2 -mb-px whitespace-nowrap transition-colors ${tab === k ? "border-[#B01E24] text-[#B01E24]" : "border-transparent text-slate-500 hover:text-slate-800"}`}>{l}</button>
        ))}
      </div>

      {tab === "reco" ? (
        <div className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
          <div className="overflow-x-auto report-scroll">
            <table className="w-full text-[12px]" data-testid="stock-reco-table">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <th rowSpan={2} className="text-left px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500 sticky left-0 bg-slate-50">Material</th>
                  <th rowSpan={2} className="text-left px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500">UoM</th>
                  <th colSpan={2} className="text-center px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-600 border-l border-slate-200">Opening</th>
                  {cats.map(([k, l]) => <th key={k} colSpan={2} className="text-center px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-600 border-l border-slate-200 whitespace-nowrap">{l}</th>)}
                  <th colSpan={2} className="text-center px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-cyan-800 border-l border-slate-200 bg-cyan-50/60">Derived Close</th>
                  <th colSpan={2} className="text-center px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-800 border-l border-slate-200 bg-emerald-50/60">Actual Close</th>
                  <th rowSpan={2} className="text-right px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-amber-800 border-l border-slate-200 bg-amber-50/60">Price Diff</th>
                </tr>
                <tr className="border-b border-slate-200 bg-slate-50 text-[9px] uppercase tracking-wider text-slate-400">
                  {["opening", ...catKeys, "derived", "actual"].map((c) => (
                    <Fragment key={c}>
                      <th className="text-right px-2 py-1 border-l border-slate-200">Qty</th>
                      <th className="text-right px-2 py-1">Value</th>
                    </Fragment>
                  ))}
                </tr>
              </thead>
              <tbody>
                {recoRows.slice(0, 300).map((r, i) => (
                  <tr key={i} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-3 py-1.5 sticky left-0 bg-white whitespace-nowrap cursor-pointer" onClick={() => setDrill(r)} data-testid={`reco-row-material-${i}`} title="Click to see all movements for this item">
                      <div className="font-mono text-[11px] text-[#B01E24] underline decoration-dotted underline-offset-2">{r.material}</div>
                      <div className="text-[10.5px] text-slate-500 max-w-[180px] truncate">{r.description} · {r.plant}</div>
                    </td>
                    <td className="px-3 py-1.5 text-slate-500">{r.uom}</td>
                    <td className="px-2 py-1.5 text-right font-mono text-slate-600 border-l border-slate-100 whitespace-nowrap">{fq(r.opening[0])}</td>
                    <td className="px-2 py-1.5 text-right font-mono text-slate-700 whitespace-nowrap">{fv(r.opening[1])}</td>
                    {catKeys.map((c) => (
                      <Fragment key={c}>
                        <td className={`px-2 py-1.5 text-right font-mono border-l border-slate-100 whitespace-nowrap ${r.cats[c][0] < 0 ? "text-amber-700" : r.cats[c][0] > 0 ? "text-emerald-700" : "text-slate-300"}`}>{fq(r.cats[c][0])}</td>
                        <td className={`px-2 py-1.5 text-right font-mono whitespace-nowrap ${r.cats[c][1] ? "text-slate-600" : "text-slate-300"}`}>{fv(r.cats[c][1])}</td>
                      </Fragment>
                    ))}
                    <td className="px-2 py-1.5 text-right font-mono text-cyan-900 border-l border-slate-100 bg-cyan-50/30 whitespace-nowrap">{fq(r.derived[0])}</td>
                    <td className="px-2 py-1.5 text-right font-mono text-cyan-900 bg-cyan-50/30 whitespace-nowrap">{fv(r.derived[1])}</td>
                    <td className="px-2 py-1.5 text-right font-mono font-semibold text-emerald-900 border-l border-slate-100 bg-emerald-50/30 whitespace-nowrap">{fq(r.actual[0])}</td>
                    <td className="px-2 py-1.5 text-right font-mono font-semibold text-emerald-900 bg-emerald-50/30 whitespace-nowrap">{fv(r.actual[1])}</td>
                    <td className={`px-3 py-1.5 text-right font-mono border-l border-slate-100 bg-amber-50/30 whitespace-nowrap ${Math.abs(r.price_diff) > 1 ? "text-amber-800 font-semibold" : "text-slate-300"}`}>{Math.abs(r.price_diff) > 1 ? formatCompact(r.price_diff) : "—"}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-slate-50 border-t-2 border-slate-300 font-semibold text-slate-900">
                  <td className="px-3 py-2 sticky left-0 bg-slate-50">TOTAL · {recoRows.length} materials</td>
                  <td className="px-3 py-2 text-slate-400">mixed</td>
                  <td className="px-2 py-2 text-right font-mono border-l border-slate-200 whitespace-nowrap">{fq(totals.opening[0])}</td>
                  <td className="px-2 py-2 text-right font-mono whitespace-nowrap">{fv(totals.opening[1])}</td>
                  {catKeys.map((c) => (
                    <Fragment key={c}>
                      <td className="px-2 py-2 text-right font-mono border-l border-slate-200 whitespace-nowrap">{fq(totals.cats[c][0])}</td>
                      <td className="px-2 py-2 text-right font-mono whitespace-nowrap">{fv(totals.cats[c][1])}</td>
                    </Fragment>
                  ))}
                  <td className="px-2 py-2 text-right font-mono border-l border-slate-200 bg-cyan-50/50 whitespace-nowrap">{fq(totals.derived[0])}</td>
                  <td className="px-2 py-2 text-right font-mono bg-cyan-50/50 whitespace-nowrap">{fv(totals.derived[1])}</td>
                  <td className="px-2 py-2 text-right font-mono border-l border-slate-200 bg-emerald-50/50 whitespace-nowrap">{fq(totals.actual[0])}</td>
                  <td className="px-2 py-2 text-right font-mono bg-emerald-50/50 whitespace-nowrap">{fv(totals.actual[1])}</td>
                  <td className="px-3 py-2 text-right font-mono text-amber-800 border-l border-slate-200 bg-amber-50/50 whitespace-nowrap">{formatCompact(totals.price_diff)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      ) : activeCat ? (
        <div className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden" data-testid="stock-cat-card">
          <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold text-slate-800">{(cats.find(([k]) => k === activeCat) || ["", activeCat])[1]} — material-wise breakdown</h3>
              <p className="text-[11px] text-slate-400">Click a material to see every movement document behind it</p>
            </div>
            <div className="text-right">
              <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">Category Total</div>
              <div className="text-lg font-bold font-mono text-slate-900">{formatCompact(catTotal[1])}</div>
            </div>
          </div>
          <div className="overflow-x-auto report-scroll">
            <table className="w-full text-[12.5px]" data-testid="stock-cat-table">
              <thead><tr className="border-b border-slate-200 bg-slate-50">
                {["Material", "UoM"].map((h) => <th key={h} className="text-left px-3 py-2.5 text-[10.5px] font-semibold uppercase tracking-wider text-slate-500">{h}</th>)}
                {["Qty", "Value", "Rate", "% of Category"].map((h) => <th key={h} className="text-right px-3 py-2.5 text-[10.5px] font-semibold uppercase tracking-wider text-slate-500">{h}</th>)}
              </tr></thead>
              <tbody>
                {catRows.slice(0, 300).map((r, i) => (
                  <tr key={i} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-3 py-2 whitespace-nowrap cursor-pointer" onClick={() => setDrill(r)} data-testid={`cat-row-material-${i}`} title="Click to see all movements">
                      <span className="font-mono text-[11px] text-[#B01E24] underline decoration-dotted underline-offset-2">{r.material}</span>
                      <span className="text-[11px] text-slate-500 ml-2">{r.description !== r.material ? r.description : ""} · {r.plant}</span>
                    </td>
                    <td className="px-3 py-2 text-slate-500">{r.uom}</td>
                    <td className={`px-3 py-2 text-right font-mono ${r.cats[activeCat][0] < 0 ? "text-amber-700" : "text-emerald-700"}`}>{fq(r.cats[activeCat][0])}</td>
                    <td className="px-3 py-2 text-right font-mono text-slate-700">{fv(r.cats[activeCat][1])}</td>
                    <td className="px-3 py-2 text-right font-mono text-slate-500">{r.cats[activeCat][0] ? fq(Math.abs(r.cats[activeCat][1] / r.cats[activeCat][0])) : "—"}</td>
                    <td className="px-3 py-2 text-right font-mono text-slate-500">{catTotal[1] ? ((100 * r.cats[activeCat][1]) / catTotal[1]).toFixed(1) : "0.0"}%</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-slate-50 border-t-2 border-slate-300 font-semibold text-slate-900">
                  <td className="px-3 py-2.5">TOTAL · {catRows.length} materials</td>
                  <td className="px-3 py-2.5 text-slate-400">mixed</td>
                  <td className="px-3 py-2.5 text-right font-mono">{fq(catTotal[0])}</td>
                  <td className="px-3 py-2.5 text-right font-mono">{fv(catTotal[1])}</td>
                  <td colSpan={2} />
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      ) : tab === "pricediff" ? (
        <div className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden" data-testid="stock-pricediff-card">
          <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold text-slate-800">Price Difference — materials where Actual ≠ Derived closing</h3>
              <p className="text-[11px] text-slate-400">Derived = Opening + movements at transaction values · Actual = book/MB5B closing value · Click a material for its movement documents</p>
            </div>
            <div className="text-right">
              <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">Total Price Difference</div>
              <div className={`text-lg font-bold font-mono ${pdTotal >= 0 ? "text-emerald-700" : "text-amber-700"}`}>{formatCompact(pdTotal)}</div>
            </div>
          </div>
          <div className="overflow-x-auto report-scroll">
            <table className="w-full text-[12.5px]" data-testid="stock-pricediff-table">
              <thead><tr className="border-b border-slate-200 bg-slate-50">
                {["Material", "UoM"].map((h) => <th key={h} className="text-left px-3 py-2.5 text-[10.5px] font-semibold uppercase tracking-wider text-slate-500">{h}</th>)}
                {["Derived Qty", "Derived Value", "Actual Qty", "Actual Value", "Price Diff", "% of Actual"].map((h) => (
                  <th key={h} className="text-right px-3 py-2.5 text-[10.5px] font-semibold uppercase tracking-wider text-slate-500 whitespace-nowrap">{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {pdRows.slice(0, 300).map((r, i) => (
                  <tr key={i} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-3 py-2 whitespace-nowrap cursor-pointer" onClick={() => setDrill(r)} data-testid={`pd-row-material-${i}`} title="Click to see all movements">
                      <span className="font-mono text-[11px] text-[#B01E24] underline decoration-dotted underline-offset-2">{r.material}</span>
                      <span className="text-[11px] text-slate-500 ml-2">{r.description !== r.material ? r.description : ""} · {r.plant}</span>
                    </td>
                    <td className="px-3 py-2 text-slate-500">{r.uom}</td>
                    <td className="px-3 py-2 text-right font-mono text-cyan-900">{fq(r.derived[0])}</td>
                    <td className="px-3 py-2 text-right font-mono text-cyan-900">{fv(r.derived[1])}</td>
                    <td className="px-3 py-2 text-right font-mono text-emerald-900">{fq(r.actual[0])}</td>
                    <td className="px-3 py-2 text-right font-mono text-emerald-900">{fv(r.actual[1])}</td>
                    <td className={`px-3 py-2 text-right font-mono font-semibold ${r.price_diff >= 0 ? "text-emerald-700" : "text-amber-700"}`}>{formatCompact(r.price_diff)}</td>
                    <td className="px-3 py-2 text-right font-mono text-slate-500">{r.actual[1] ? ((100 * r.price_diff) / Math.abs(r.actual[1])).toFixed(1) : "—"}%</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-slate-50 border-t-2 border-slate-300 font-semibold text-slate-900">
                  <td className="px-3 py-2.5">TOTAL · {pdRows.length} materials</td>
                  <td colSpan={5} className="px-3 py-2.5 text-slate-400">—</td>
                  <td className={`px-3 py-2.5 text-right font-mono ${pdTotal >= 0 ? "text-emerald-800" : "text-amber-800"}`}>{formatCompact(pdTotal)}</td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      ) : tab === "margin" ? (
        !data.margin ? (
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-5 text-sm text-amber-800" data-testid="stock-margin-empty">
            Billing data not available — the SAP Billing Document service (API_BILLING_DOCUMENT_SRV) did not respond.
            Check it under API Service Mapping below in Settings, or adjust the billing types (default F2, F1, RE, G2, L2).
          </div>
        ) : (
          <div className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden" data-testid="stock-margin-card">
            <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold text-slate-800">Transaction Margin — Billing vs Goods Issue</h3>
                <p className="text-[11px] text-slate-400">Billed value from F2 / returns / credit &amp; debit notes vs COGS from 601/602 goods-issue values (MB51)</p>
              </div>
              <div className="text-right">
                <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">Total Margin</div>
                <div className={`text-lg font-bold font-mono ${marginTotals.margin >= 0 ? "text-emerald-700" : "text-red-700"}`}>
                  {formatCompact(marginTotals.margin)} <span className="text-xs text-slate-400">({marginTotals.margin_pct}%)</span>
                </div>
              </div>
            </div>
            <div className="overflow-x-auto report-scroll">
              <table className="w-full text-[12.5px]" data-testid="stock-margin-table">
                <thead><tr className="border-b border-slate-200 bg-slate-50">
                  <th className="text-left px-3 py-2.5 text-[10.5px] font-semibold uppercase tracking-wider text-slate-500">Material</th>
                  <th className="text-right px-3 py-2.5 text-[10.5px] font-semibold uppercase tracking-wider text-slate-500">Billed Qty</th>
                  <th className="text-right px-3 py-2.5 text-[10.5px] font-semibold uppercase tracking-wider text-slate-500">Billed Value</th>
                  <th className="text-right px-3 py-2.5 text-[10.5px] font-semibold uppercase tracking-wider text-slate-500">COGS Qty (601/602)</th>
                  <th className="text-right px-3 py-2.5 text-[10.5px] font-semibold uppercase tracking-wider text-slate-500">COGS Value</th>
                  <th className="text-right px-3 py-2.5 text-[10.5px] font-semibold uppercase tracking-wider text-slate-500">Margin</th>
                  <th className="text-right px-3 py-2.5 text-[10.5px] font-semibold uppercase tracking-wider text-slate-500">Margin %</th>
                  <th className="text-right px-3 py-2.5 text-[10.5px] font-semibold uppercase tracking-wider text-slate-500">Docs</th>
                </tr></thead>
                <tbody>
                  {marginRows.map((r) => (
                    <tr key={r.material} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span className="font-mono text-[11px] text-[#B01E24]">{r.material}</span>
                        <span className="text-slate-600 ml-2">{r.description !== r.material ? r.description : ""}</span>
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-slate-600">{r.billed_qty.toLocaleString("en-IN")}</td>
                      <td className="px-3 py-2 text-right font-mono text-slate-700">{formatCompact(r.billed_value)}</td>
                      <td className="px-3 py-2 text-right font-mono text-slate-600">{r.cogs_qty.toLocaleString("en-IN")}</td>
                      <td className="px-3 py-2 text-right font-mono text-slate-700">{formatCompact(r.cogs_value)}</td>
                      <td className={`px-3 py-2 text-right font-mono font-semibold ${r.margin >= 0 ? "text-emerald-700" : "text-red-700"}`}>{formatCompact(r.margin)}</td>
                      <td className={`px-3 py-2 text-right font-mono ${r.margin_pct >= 0 ? "text-emerald-700" : "text-red-700"}`}>{r.margin_pct}%</td>
                      <td className="px-3 py-2 text-right font-mono text-slate-400">{r.docs}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-slate-50 border-t border-slate-200 font-semibold text-[12px] text-slate-900">
                    <td className="px-3 py-2.5">TOTAL</td>
                    <td className="px-3 py-2.5 text-right font-mono">{marginTotals.billed_qty.toLocaleString("en-IN")}</td>
                    <td className="px-3 py-2.5 text-right font-mono">{formatCompact(marginTotals.billed_value)}</td>
                    <td className="px-3 py-2.5 text-right font-mono">{marginTotals.cogs_qty.toLocaleString("en-IN")}</td>
                    <td className="px-3 py-2.5 text-right font-mono">{formatCompact(marginTotals.cogs_value)}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-emerald-800">{formatCompact(marginTotals.margin)}</td>
                    <td className="px-3 py-2.5 text-right font-mono">{marginTotals.margin_pct}%</td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )
      ) : (
        <div className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
          <div className="overflow-x-auto report-scroll">
            <table className="w-full text-[13px]" data-testid="stock-mb51-table">
              <thead><tr className="border-b border-slate-200 bg-slate-50">
                {["Posting Date", "Mat. Doc", "Mvt Type", "Category", "Material", "Plant"].map((h) => (
                  <th key={h} className="text-left px-3 py-2.5 text-[10.5px] font-semibold uppercase tracking-wider text-slate-500 whitespace-nowrap">{h}</th>
                ))}
                <th className="text-right px-3 py-2.5 text-[10.5px] font-semibold uppercase tracking-wider text-slate-500">Qty</th>
                <th className="text-right px-3 py-2.5 text-[10.5px] font-semibold uppercase tracking-wider text-slate-500">Value</th>
              </tr></thead>
              <tbody>
                {movRows.map((m, i) => (
                  <tr key={i} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-3 py-2 text-slate-500 whitespace-nowrap">{fmtD(m.date)}</td>
                    <td className="px-3 py-2 font-mono text-[12px] text-slate-500">{m.mat_doc}</td>
                    <td className="px-3 py-2 font-mono text-[12px] text-slate-700">{m.mtype}</td>
                    <td className="px-3 py-2"><span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200">{(cats.find(([k]) => k === m.category) || ["", m.category])[1]}</span></td>
                    <td className="px-3 py-2 font-mono text-[12px] text-[#B01E24] whitespace-nowrap cursor-pointer underline decoration-dotted underline-offset-2" data-testid={`mb51-row-material-${i}`} onClick={() => setDrill({ material: m.material, plant: m.plant, description: m.description })}>{m.material}</td>
                    <td className="px-3 py-2 text-slate-500 whitespace-nowrap">{m.plant}</td>
                    <td className={`px-3 py-2 text-right font-mono text-[12px] ${m.qty < 0 ? "text-amber-700" : "text-emerald-700"}`}>{m.qty.toLocaleString("en-IN")}</td>
                    <td className="px-3 py-2 text-right font-mono text-[12px] text-slate-700">{formatINR(m.value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-2 text-[11px] text-slate-400 border-t border-slate-100">{movRows.length} movement lines shown</div>
        </div>
      )}

      <ItemMovementsDialog item={drill} fromMonth={fromM} toMonth={toM} onClose={() => setDrill(null)} />
    </div>
  );
}
